import { Box } from "@mui/material";
import { GridPaginationModel, GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import FilterSelect from "../../../../../components/FilterSelect";
import Flex from "../../../../../components/Flex";
import SearchInput from "../../../../../components/SearchInput";
import StatusChip from "../../../../../components/StatusChip";
import Text from "../../../../../components/Text";
import { useDebounce } from "../../../../../hooks/use-debounce";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useGetPatientInquiryByUuid } from "../../../../../queries/collaboration/patient-inquiry/use-get-patient-inquiry-by-uuid";
import { useListPatientInquiry } from "../../../../../queries/collaboration/patient-inquiry/use-list-patient-inquiry";
import ProviderSelectScroll from "@/components/ProviderSelectScroll";
import { formatDateTime } from "../../../../../utils/date-time-utility";
import { PatientName } from "../../../../../utils/util-components/UtilComponents";
import {
  DEF_PAGINATION,
  applyDefaultColumnSettings,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "../../../../../utils/utils";
import PatientInquiryDetailsDialog from "./PatientInquiryDetailsDialog";
import {
  PATIENT_INQUIRY_STATUS_PARAM,
  PATIENT_TYPE_FILTER_OPTIONS,
  SOURCE_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
  // STATUS_OPTIONS,
  getStatusUi,
} from "./patient-inquiry-constants";

const getAnyValue = (obj: Record<string, any>, keys: string[]) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const getSource = (row: Record<string, any>) =>
  String(getAnyValue(row, ["source", "inquirySource"]) ?? "").toUpperCase();

const getPatientDob = (row: Record<string, any>) =>
  String(getAnyValue(row, ["patientDob", "dob", "birthDate"]) ?? "");

const getSourceUi = (source: string) => {
  if (source === "WEBSITE") {
    return { label: "Website", color: "#6C4CE3", bgColor: "#F3EEFF" };
  }
  if (source === "PORTAL") {
    return { label: "Portal", color: "#D84B75", bgColor: "#FFEAF1" };
  }
  return {
    label: source ? replaceUnderScoreAddSpace(source.toLowerCase()) : "-",
    color: "#616161",
    bgColor: "#F2F4F7",
  };
};

const PatientInquiries = () => {
  const xTENANTID = getXtenantId();

  const [pagination, setPagination] =
    useState<GridPaginationModel>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const [searchString, setSearchString, instantSearchString] =
    useDebounce<string>("");
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Status is read from the URL rather than local state so a caller can land
   * here with the filter already applied (the scheduling badge opens
   * `?status=PENDING`). An unrecognised value reads as "no filter" so an
   * edited URL cannot put the dropdown into a state the user cannot see.
   */
  const statusParam = (
    searchParams.get(PATIENT_INQUIRY_STATUS_PARAM) ?? ""
  ).toUpperCase();
  const selectedStatus = STATUS_FILTER_OPTIONS.some(
    (option) => option.value === statusParam
  )
    ? statusParam
    : "";

  /** Replace, not push — the filter is not a step in the back history. */
  const setSelectedStatus = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(PATIENT_INQUIRY_STATUS_PARAM, value);
        else next.delete(PATIENT_INQUIRY_STATUS_PARAM);
        return next;
      },
      { replace: true }
    );
  };

  const [selectedPatientType, setSelectedPatientType] = useState<string>("");
  const [selectedSource, setSelectedSource] = useState<string>("");
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const inqueryDisc = useDisclosure();

  const source_mapper: Record<string, string> = {
    WEBSITE: "Website",
    PORTAL: "Patient Portal",
  };

  const selectedSource_mapper =
    source_mapper[selectedSource as keyof typeof source_mapper];

  const { data: inquiryList, isFetching: inquiryListLoading } =
    useListPatientInquiry({
      pagination,
      gridSorting,
      providerId: selectedProvider?.value || undefined,
      status: selectedStatus ? ([selectedStatus] as any) : undefined,
      searchString: searchString || undefined,
      source: selectedSource_mapper
        ? (selectedSource_mapper as any)
        : undefined,
      xTENANTID,
    });

  const { data: inquiryDetailsRes, isFetching: inquiryDetailsLoading } =
    useGetPatientInquiryByUuid({
      inquiryId: inqueryDisc.data,
      xTENANTID,
    });

  const COLUMNS = applyDefaultColumnSettings(
    [
      {
        field: "serialNo",
        headerName: "Sr.No",
        minWidth: 50,
        flex: 0.05,
        sortable: false,
        renderCell: (params) =>
          params.api.getAllRowIds().indexOf(params.id) + 1,
      },
      {
        field: "patientName",
        headerName: "Patient Name",
        minWidth: 190,
        flex: 0.14,
        renderCell: ({ row }: any) => {
          const patientName =
            getAnyValue(row, [
              "patientName",
              "name",
              "fullName",
              "patientFullName",
            ]) ?? "-";
          return (
            <PatientName
              disableNavigate
              name={patientName}
              patientId=""
              birthDate={getPatientDob(row) || undefined}
              gender={row?.patientGender ?? ""}
            />
          );
        },
      },
      {
        field: "inquiryChange",
        headerName: "Patient Type",
        minWidth: 130,
        flex: 0.09,
        renderCell: ({ value }: any) => {
          return (
            <Text isTabletext>
              {value ? replaceUnderScoreAddSpace(value.toLowerCase()) : "-"}
            </Text>
          );
        },
      },
      {
        field: "source",
        headerName: "Source",
        minWidth: 130,
        flex: 0.1,
        renderCell: ({ row }: any) => {
          const source = getSource(row);
          const sourceUi = getSourceUi(source);
          return (
            <StatusChip
              label={sourceUi.label}
              color={sourceUi.color}
              bgColor={sourceUi.bgColor}
            />
          );
        },
      },
      {
        field: "created",
        headerName: "Inquiry Submitted On",
        minWidth: 165,
        flex: 0.07,
        renderCell: ({ value }: any) => (
          <Text isTabletext>{formatDateTime(value)}</Text>
        ),
      },
      {
        field: "isScheduled",
        headerName: "Appointment Scheduled?",
        align: "center",
        minWidth: 120,
        flex: 0.12,
        renderCell: ({ row }: any) => (
          <Text isTabletext>
            {getAnyValue(row, [
              "status",
              "isAppointmentScheduled",
              "scheduled",
            ]) === "CONFIRMED"
              ? "Yes"
              : "No"}
          </Text>
        ),
      },
      {
        field: "appDateAndTime",
        headerName: "Appointment Date & Time",
        minWidth: 190,
        flex: 0.14,
        renderCell: ({ row }: any) => {
          return getAnyValue(row, [
            "status",
            "isAppointmentScheduled",
            "scheduled",
          ]) === "CONFIRMED" ? (
            <Text isTabletext>{formatDateTime(row?.appDateAndTime)}</Text>
          ) : (
            "-"
          );
        },
      },
      {
        field: "provider",
        headerName: "Provider",
        minWidth: 180,
        flex: 0.14,
        renderCell: ({ row }: any) => {
          const providerName =
            getAnyValue(row, ["providerName", "assignedProviderName"]) ?? "-";
          return (
            <Flex flexDirection="column" lineHeight={1.2}>
              <Text isTabletext>{providerName}</Text>
            </Flex>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 130,
        flex: 0.11,
        renderCell: ({ row }: any) => {
          const status = String(getAnyValue(row, ["status"]) ?? "");
          const statusUi = getStatusUi(status);
          return (
            <StatusChip
              label={statusUi.label}
              color={statusUi.color}
              bgColor={statusUi.bgColor}
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "Action",
        minWidth: 130,
        flex: 0.09,
        sortable: false,
        renderCell: ({ row }: any) => (
          <Text
            isTabletext
            color="primary.main"
            fontWeight="medium"
            sx={{ cursor: "pointer" }}
            onClick={() => inqueryDisc.open(row.uuid)}
          >
            View Details
          </Text>
        ),
      },
    ],
    ["patientName", "submittedOn", "source", "patientType", "status"]
  );

  return (
    <Box width="100%" height="100%" display="flex" flexDirection="column">
      <Flex mb={1.5} alignItems="center" justifyContent="space-between" gap={1}>
        <Text fontWeight="medium" fontSize="18px">
          Patient Inquiries
        </Text>

        <Flex
          gap={1}
          flexWrap="wrap"
          alignItems="center"
          justifyContent="flex-end"
        >
          <Box width="14.5rem">
            <SearchInput
              searchKey={instantSearchString}
              onSearch={(v) => setSearchString(v)}
              onClear={() => setSearchString("")}
              placeholder="Search by patient name, patient type,.."
            />
          </Box>
          <Box width="9rem">
            <FilterSelect
              placeholder="Status"
              value={selectedStatus || null}
              options={STATUS_FILTER_OPTIONS}
              onChange={(value) => setSelectedStatus(value || "")}
              clearable
            />
          </Box>
          <Box width="9rem">
            <FilterSelect
              placeholder="Patient Type"
              value={selectedPatientType || null}
              options={PATIENT_TYPE_FILTER_OPTIONS}
              onChange={(value) => setSelectedPatientType(value || "")}
              clearable
            />
          </Box>
          <Box width="8rem">
            <FilterSelect
              placeholder="Source"
              value={selectedSource || null}
              options={SOURCE_FILTER_OPTIONS}
              onChange={(value) => setSelectedSource(value || "")}
              clearable
            />
          </Box>
          <Box width="9rem">
            <ProviderSelectScroll
              value={selectedProvider}
              onChange={(value) => setSelectedProvider(value)}
              viewBirthDate={false}
              isMatchButtonHeight
            />
          </Box>
        </Flex>
      </Flex>

      <Box flex={1} overflow={"hidden"}>
        <CustomDataGridV2
          loading={inquiryListLoading}
          rows={inquiryList?.data?.content ?? []}
          rowCount={inquiryList?.data?.page?.totalElements ?? 0}
          getRowId={(row) => row.uuid}
          columns={COLUMNS}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
          sortModel={gridSorting}
          searchString={instantSearchString}
        />
      </Box>

      <PatientInquiryDetailsDialog
        disclosureHost={inqueryDisc}
        inquiryId={inqueryDisc.data}
        xTENANTID={xTENANTID}
        inquiryDetails={inquiryDetailsRes?.data}
      />

      {inquiryDetailsLoading && !!inqueryDisc.data && (
        <Text mt={1}>Loading patient inquiry details...</Text>
      )}
    </Box>
  );
};

export default PatientInquiries;
